/**
 * 의약품 월간 수집 배치를 테이블 단위로 실행하는 선택 모달.
 * 5종을 모두 고르면 targetTables 없이 전체 수집으로 보낸다(인덱스·배치 완료 플래그까지 실행).
 */
import {
  DATA_COLLECTION_TARGET_TABLES,
  type DataCollectionTargetTable,
} from '@/entities/drug-data/api/drug-data.api';
import { Button } from '@/shared/components/ui/button';
import { Checkbox } from '@/shared/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { AlertCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface DataCollectionTableSelectModalProps {
  open: boolean;
  onClose: () => void;
  /** 전체 선택이면 undefined, 일부 선택이면 선택한 테이블 목록 */
  onConfirm: (targetTables: DataCollectionTargetTable[] | undefined) => void;
}

const ALL_TABLES = DATA_COLLECTION_TARGET_TABLES.map((t) => t.value);

export const DataCollectionTableSelectModal: React.FC<
  DataCollectionTableSelectModalProps
> = ({ open, onClose, onConfirm }) => {
  const [selected, setSelected] = useState<DataCollectionTargetTable[]>([]);

  // 열 때마다 선택을 비워, 이전에 고른 테이블로 실수 실행하지 않게 한다
  useEffect(() => {
    if (open) setSelected([]);
  }, [open]);

  const allSelected = selected.length === ALL_TABLES.length;
  const isPartial = selected.length > 0 && !allSelected;

  const toggle = (table: DataCollectionTargetTable, checked: boolean) =>
    setSelected((prev) =>
      checked
        ? ALL_TABLES.filter((t) => t === table || prev.includes(t))
        : prev.filter((t) => t !== table),
    );

  const handleConfirm = () => {
    onConfirm(allSelected ? undefined : selected);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>원천 데이터 수집</DialogTitle>
          <DialogDescription>
            수집할 원천을 선택하세요. 선택한 원천만 temp 테이블로 새로
            가져옵니다.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <label className="flex items-center gap-2 border-b border-border pb-3 text-sm font-medium">
            <Checkbox
              checked={allSelected}
              onCheckedChange={(v) => setSelected(v === true ? ALL_TABLES : [])}
            />
            전체
          </label>
          {DATA_COLLECTION_TARGET_TABLES.map((table) => (
            <label
              key={table.value}
              className="flex items-center gap-2 text-sm"
            >
              <Checkbox
                checked={selected.includes(table.value)}
                onCheckedChange={(v) => toggle(table.value, v === true)}
              />
              <span>{table.label}</span>
              <span className="text-xs text-muted-foreground">
                {table.value}
              </span>
            </label>
          ))}
        </div>

        {isPartial ? (
          <p className="flex items-start gap-1.5 text-xs text-ruda-warning">
            <AlertCircle className="mt-0.5 size-3.5 shrink-0" />
            부분 수집은 배치 완료 플래그를 갱신하지 않아 의약품 데이터 공개가
            막힐 수 있습니다. 그 달 전체 수집이 이미 완료됐다면 플래그는
            유지됩니다.
          </p>
        ) : null}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            취소
          </Button>
          <Button disabled={selected.length === 0} onClick={handleConfirm}>
            {allSelected ? '전체 수집' : '선택 수집'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
